/**
 * The proposed fix on a finding page: stat tiles that jump to their
 * section, then every change Inspector proposes as its word-by-word wording,
 * always visible. Nothing here touches a PDF: the before/after page crops
 * open per change in `ChangeReviewDialog`, loaded (with pdf.js) only when
 * it is first opened.
 *
 * View-only: the decision is the page's Apply fix (the whole proposal).
 *
 * @module components/regulation/finding/changes/ProposedFixChanges
 */
import type { DocumentChangesView, FindingPdfEdit } from "@repo/shared/findingChanges";
import { ArrowUpRightIcon, FileDiffIcon, FileSearchIcon, ListChecksIcon } from "lucide-react";
import { lazy, Suspense, useState, type ReactNode } from "react";

import { documentChangesFileUrl } from "../../../../state/findingChanges";
import { InspectorIcon } from "../../../InspectorIcon";
import { Button } from "../../../ui/button";
import { Hint, PropertyChip } from "../findingGlyphs";
import type { ChangeDiscussion } from "./changeDiscussion";
import { InlineDiff } from "./InlineDiff";
import { editKind, pageLabel, splitSection } from "./proposedFix";

const ChangeReviewDialog = lazy(() => import("./ChangeReviewDialog").then((module) => ({ default: module.ChangeReviewDialog })));

/** Where the checklist section (rendered by the page) can be found. */
export const FIX_CHECKLIST_SELECTOR = "[data-finding-fix-checklist]";

const scrollTo = (selector: string) => window.document.querySelector(selector)?.scrollIntoView({ block: "start", behavior: "smooth" });

interface FixStat {
  readonly icon: ReactNode;
  readonly value: string;
  readonly label: string;
  readonly note?: string | undefined;
  readonly target: string;
}

/** Stat tiles above the fix; each scrolls to the section it counts. */
function FixStats({ items }: { readonly items: ReadonlyArray<FixStat> }) {
  return (
    <div className="grid grid-cols-2 gap-2" data-fix-stats="">
      {items.map((item) => (
        <button
          key={item.label}
          type="button"
          onClick={() => scrollTo(item.target)}
          className="flex h-16 cursor-pointer flex-col justify-center gap-1 rounded-lg border border-border/70 bg-card px-4 text-left outline-none transition-colors duration-150 hover:bg-foreground/[0.03] focus-visible:ring-2 focus-visible:ring-ring"
          data-fix-stat={item.label.toLowerCase()}
        >
          <span className="flex items-baseline gap-2">
            <span className="truncate text-lg font-semibold leading-6 tabular-nums">{item.value}</span>
            {item.note ? <span className="truncate text-xs text-muted-foreground">{item.note}</span> : null}
          </span>
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground [&_svg]:size-3.5 [&_svg]:shrink-0">
            {item.icon}
            {item.label}
          </span>
        </button>
      ))}
    </div>
  );
}

/** One change: its section and page, the word-by-word change, and the way to its PDF pages. */
function ChangeCard({
  edit,
  index,
  onOpen,
  onAsk,
}: {
  readonly edit: FindingPdfEdit;
  readonly index: number;
  readonly onOpen: (index: number) => void;
  readonly onAsk?: (() => void) | undefined;
}) {
  const { number, name } = splitSection(edit.section);
  const kind = editKind(edit);
  return (
    <section aria-label={`Change ${index + 1}: ${name}`} className="overflow-hidden rounded-lg border border-border/70 bg-card" data-change-card={index}>
      <header className="flex h-12 items-center gap-3 border-b border-border/70 px-4">
        <span className="shrink-0 text-[13px] tabular-nums text-muted-foreground">{index + 1}</span>
        <Hint content={<p className="font-medium text-foreground">{edit.section}</p>} render={<h3 className="min-w-0 flex-1 truncate text-[13px] font-medium" />}>
          {name}
        </Hint>
        <div className="hidden shrink-0 items-center gap-1.5 sm:flex">
          {number ? <PropertyChip>§ {number}</PropertyChip> : null}
          <PropertyChip>{pageLabel(edit)}</PropertyChip>
          {kind === "changed" ? null : <PropertyChip>{kind === "added" ? "New passage" : "Removed"}</PropertyChip>}
        </div>
      </header>
      <button
        type="button"
        onClick={() => onOpen(index)}
        className="block w-full cursor-pointer px-4 py-3 text-left outline-none transition-colors duration-150 hover:bg-foreground/[0.02] focus-visible:bg-foreground/[0.03]"
        aria-label={`Open change ${index + 1} in the PDF`}
        data-change-passage=""
      >
        <InlineDiff before={edit.beforeText} after={edit.afterText} className="line-clamp-4 text-[13px] leading-6" />
      </button>
      <footer className="flex h-11 items-center gap-1 border-t border-border/70 px-2">
        <Button size="sm" variant="ghost-muted" onClick={() => onOpen(index)} data-open-pdf="">
          <FileSearchIcon aria-hidden />
          View in PDF
        </Button>
        {onAsk ? (
          <Button size="sm" variant="ghost-muted" onClick={onAsk} data-ask-about-change="">
            <InspectorIcon ink="inherit" />
            <span className="max-sm:sr-only">Ask Inspector about this change</span>
          </Button>
        ) : null}
      </footer>
    </section>
  );
}

export function ProposedFixChanges({
  organizationId,
  proposal,
  checklistDone = 0,
  status,
  onAskAboutChange,
}: {
  readonly organizationId: string;
  readonly proposal: DocumentChangesView;
  /** Checks already met, when the checklist records them; the tile shows done/total. */
  readonly checklistDone?: number | undefined;
  /** Beside the change count, e.g. "Fix needs update". */
  readonly status?: ReactNode;
  /** Puts a question about one change into the page's composer; without it the action is hidden. */
  readonly onAskAboutChange?: ((context: ChangeDiscussion) => void) | undefined;
}) {
  const [reviewing, setReviewing] = useState<number | null>(null);
  // The dialog (and pdf.js behind it) is fetched the first time it opens, never on page load.
  const [dialogLoaded, setDialogLoaded] = useState(false);
  const open = (index: number | null) => {
    if (index !== null) setDialogLoaded(true);
    setReviewing(index);
  };
  const total = proposal.edits.length;
  const checks = proposal.outstandingChecks.length;
  const stats: FixStat[] = [
    { icon: <FileDiffIcon />, value: String(total), label: total === 1 ? "Change" : "Changes", target: "[data-change-panel]" },
    ...(checks
      ? [{ icon: <ListChecksIcon />, value: `${Math.min(checklistDone, checks)}/${checks}`, label: "Checklist", target: FIX_CHECKLIST_SELECTOR }]
      : []),
  ];
  return (
    <div className="space-y-4">
      <FixStats items={stats} />
      <div className="space-y-3" data-change-panel="">
        {/* A hairline card like the others, translucent while it sticks; no drop shadow. */}
        <div className="sticky top-2 z-20 rounded-lg border border-border/70 bg-card/90 backdrop-blur-md">
          <div className="flex h-11 items-center gap-3 pe-2 ps-4" data-change-bar="">
            <p className="text-[13px] font-medium tabular-nums" data-change-summary="">
              {total} {total === 1 ? "change" : "changes"}
            </p>
            {status}
            <div className="ms-auto flex items-center gap-1">
              <Button size="sm" variant="ghost" onClick={() => open(0)} data-review-pdf="">
                <FileSearchIcon aria-hidden />
                <span className="sm:hidden">Review</span>
                <span className="max-sm:hidden">Review in PDF</span>
              </Button>
              <Button
                size="sm"
                variant="ghost-muted"
                className="max-sm:hidden"
                render={<a href={documentChangesFileUrl(organizationId, proposal, "proposed")} target="_blank" rel="noopener noreferrer" />}
              >
                Proposed PDF
                <ArrowUpRightIcon aria-hidden />
              </Button>
            </div>
          </div>
        </div>
        <div className="space-y-4">
          {proposal.edits.map((edit, index) => (
            <ChangeCard
              key={`${proposal.id}:${index}`}
              edit={edit}
              index={index}
              onOpen={open}
              onAsk={onAskAboutChange ? () => onAskAboutChange({ proposalId: proposal.id, index, edit }) : undefined}
            />
          ))}
        </div>
      </div>
      {dialogLoaded ? (
        <Suspense fallback={null}>
          <ChangeReviewDialog organizationId={organizationId} proposal={proposal} index={reviewing} onIndexChange={open} />
        </Suspense>
      ) : null}
    </div>
  );
}
